import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import swal from "sweetalert";
import Loading from "../../components/loading";
import Pagination from "../../components/Pagination";
import {
    AKFA_PAGE_BASE,
    getAkfaMessageById,
    getAkfaMessages,
    recalculateAkfaRates,
    retryAkfaMessage,
} from "../../services/akfa/akfaService";
import { AKFA_PAGE_SIZE, SELECT_CLASS, formatDateTime, showError, showSuccess, toPageState } from "./akfaUtils";
import useFillViewportHeight from "./useFillViewportHeight";

const MESSAGE_STATUSES = ["PROCESSED", "PARTIAL", "FAILED"];
const MESSAGE_TYPES = ["PRODUCT", "PRODUCT_LIST", "PRODUCT_RATE"];
const RETRYABLE_STATUSES = ["FAILED", "PARTIAL"];

const MESSAGE_STATUS_STYLES = {
    PROCESSED: "bg-green-100 text-green-800",
    PARTIAL: "bg-yellow-100 text-yellow-800",
    FAILED: "bg-red-100 text-red-800",
};

const prettyBody = (body) => {
    if (body === null || body === undefined) return "—";
    if (typeof body !== "string") return JSON.stringify(body, null, 2);
    try {
        return JSON.stringify(JSON.parse(body), null, 2);
    } catch {
        return body;
    }
};

export default function AkfaLogSettingsTab({ refreshSummary }) {
    const { t } = useTranslation();
    const { token } = useSelector((state) => state.user);

    const [pageState, setPageState] = useState({ items: [], total: 0, pageCount: 0 });
    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [filterStatus, setFilterStatus] = useState("");
    const [filterType, setFilterType] = useState("");
    const [retryingId, setRetryingId] = useState(null);
    const [recalculating, setRecalculating] = useState(false);
    const [detail, setDetail] = useState({ open: false, loading: false, message: null });
    const [tableRef, tableHeight] = useFillViewportHeight();

    const fetchData = async () => {
        setIsLoading(true);
        const response = await getAkfaMessages(token, {
            status: filterStatus,
            type: filterType,
            page: page + AKFA_PAGE_BASE,
            size: AKFA_PAGE_SIZE,
        });
        if (response.success) {
            setPageState(toPageState(response.data, AKFA_PAGE_SIZE));
        } else {
            showError(response, t("akfaIntegrationPage.common.load_error"));
        }
        setIsLoading(false);
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, filterStatus, filterType]);

    const handleRetry = async (message) => {
        setRetryingId(message.id);
        const response = await retryAkfaMessage(token, message.id);
        setRetryingId(null);
        if (response.success) {
            showSuccess(t("akfaIntegrationPage.logSettings.retry_success"));
            fetchData();
            refreshSummary();
        } else {
            showError(response, t("akfaIntegrationPage.common.action_error"));
        }
    };

    const openDetail = async (message) => {
        setDetail({ open: true, loading: true, message });
        const response = await getAkfaMessageById(token, message.id);
        if (response.success) {
            setDetail({ open: true, loading: false, message: response.data });
        } else {
            setDetail({ open: true, loading: false, message });
            showError(response, t("akfaIntegrationPage.common.load_error"));
        }
    };

    const handleRecalculate = async () => {
        const confirmed = await swal({
            title: t("akfaIntegrationPage.logSettings.recalculate_confirm_title"),
            text: t("akfaIntegrationPage.logSettings.recalculate_confirm_text"),
            icon: "warning",
            buttons: { cancel: true, confirm: true },
        });
        if (!confirmed) return;
        setRecalculating(true);
        const response = await recalculateAkfaRates(token);
        setRecalculating(false);
        if (response.success) {
            showSuccess(t("akfaIntegrationPage.logSettings.recalculate_started"));
        } else {
            showError(response, t("akfaIntegrationPage.common.action_error"));
        }
    };

    const changeFilter = (setter) => (e) => {
        setPage(0);
        setter(e.target.value);
    };

    const items = pageState.items;

    return (
        <div className="py-4">
            <div className="mb-4 flex flex-wrap items-center gap-3">
                <select value={filterStatus} onChange={changeFilter(setFilterStatus)} className={SELECT_CLASS}>
                    <option value="">{t("akfaIntegrationPage.logSettings.filter_status_all")}</option>
                    {MESSAGE_STATUSES.map((s) => (
                        <option key={s} value={s}>
                            {s}
                        </option>
                    ))}
                </select>
                <select value={filterType} onChange={changeFilter(setFilterType)} className={SELECT_CLASS}>
                    <option value="">{t("akfaIntegrationPage.logSettings.filter_type_all")}</option>
                    {MESSAGE_TYPES.map((type) => (
                        <option key={type} value={type}>
                            {type}
                        </option>
                    ))}
                </select>
                <Link
                    to="/integrations/unit"
                    className="ml-auto px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50"
                >
                    {t("akfaUnitMappingPage.title")}
                </Link>
                <button
                    onClick={handleRecalculate}
                    disabled={recalculating}
                    className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-500 disabled:opacity-60"
                >
                    {recalculating
                        ? t("akfaIntegrationPage.common.loading")
                        : t("akfaIntegrationPage.logSettings.recalculate_button")}
                </button>
            </div>

            <h3 className="text-sm font-semibold text-gray-900 mb-3">{t("akfaIntegrationPage.logSettings.events_title")}</h3>

            <div
                ref={tableRef}
                style={{ height: tableHeight ?? undefined }}
                className="overflow-auto shadow ring-1 ring-black ring-opacity-5 md:rounded-lg"
            >
                {isLoading ? (
                    <Loading />
                ) : (
                        <table className="min-w-full divide-y divide-gray-300">
                            <thead className="sticky top-0 z-[1] text-white bg-gray-700">
                                <tr>
                                    <th className="py-3 pl-4 pr-3 text-left text-sm font-semibold">
                                        {t("akfaIntegrationPage.logSettings.col_received")}
                                    </th>
                                    <th className="py-3 pl-2 pr-3 text-left text-sm font-semibold">
                                        {t("akfaIntegrationPage.logSettings.col_type")}
                                    </th>
                                    <th className="py-3 pl-2 pr-3 text-left text-sm font-semibold">
                                        {t("akfaIntegrationPage.logSettings.col_queue")}
                                    </th>
                                    <th className="py-3 pl-2 pr-3 text-left text-sm font-semibold">
                                        {t("akfaIntegrationPage.logSettings.col_items")}
                                    </th>
                                    <th className="py-3 pl-2 pr-3 text-left text-sm font-semibold">
                                        {t("akfaIntegrationPage.common.status_label")}
                                    </th>
                                    <th className="py-3 pl-2 pr-3 text-left text-sm font-semibold">
                                        {t("akfaIntegrationPage.logSettings.col_processed")}
                                    </th>
                                    <th className="relative py-3 pl-3 pr-4">
                                        <span className="sr-only">{t("akfaIntegrationPage.common.actions")}</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-300">
                                {items.length > 0 ? (
                                    items.map((message) => (
                                        <tr
                                            key={message.id}
                                            className={message.status === "FAILED" ? "bg-red-50" : "hover:bg-gray-50"}
                                        >
                                            <td className="pl-4 px-2 py-2 text-sm text-gray-700">
                                                {formatDateTime(message.receivedAt)}
                                            </td>
                                            <td className="px-2 py-2 text-sm text-gray-700">{message.messageType}</td>
                                            <td className="px-2 py-2 text-xs text-gray-500 break-all">{message.queue}</td>
                                            <td className="px-2 py-2 text-sm text-gray-700">
                                                {message.itemCount ?? "—"}
                                                {message.failedCount ? (
                                                    <span className="text-red-700"> / {message.failedCount} ✕</span>
                                                ) : null}
                                            </td>
                                            <td className="px-2 py-2 text-sm">
                                                <span
                                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                        MESSAGE_STATUS_STYLES[message.status] || "bg-gray-100 text-gray-800"
                                                    }`}
                                                >
                                                    {message.status}
                                                </span>
                                                {message.attempts > 1 && (
                                                    <span className="ml-1 text-xs text-gray-400">×{message.attempts}</span>
                                                )}
                                                {message.error && (
                                                    <div className="text-xs text-red-700 mt-1">{message.error}</div>
                                                )}
                                            </td>
                                            <td className="px-2 py-2 text-sm text-gray-700">
                                                {formatDateTime(message.processedAt)}
                                            </td>
                                            <td className="relative py-2 text-right text-sm font-medium pr-4">
                                                <div className="flex justify-end gap-2">
                                                    <button
                                                        onClick={() => openDetail(message)}
                                                        className="px-3 py-1 bg-white border border-gray-300 text-gray-700 rounded-md text-xs hover:bg-gray-50"
                                                    >
                                                        {t("akfaIntegrationPage.logSettings.detail_button")}
                                                    </button>
                                                    {RETRYABLE_STATUSES.includes(message.status) && (
                                                        <button
                                                            onClick={() => handleRetry(message)}
                                                            disabled={retryingId === message.id}
                                                            className="px-3 py-1 bg-indigo-600 text-white rounded-md text-xs hover:bg-indigo-500 disabled:opacity-60"
                                                        >
                                                            {t("akfaIntegrationPage.logSettings.retry_button")}
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={7} className="py-8 text-center text-sm text-gray-500">
                                            {t("akfaIntegrationPage.logSettings.empty")}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                )}
            </div>

            {pageState.pageCount > 1 && (
                <Pagination
                    handlePageClick={(e) => setPage(e.selected)}
                    pageCount={pageState.pageCount}
                    forcePage={page}
                />
            )}

            {detail.open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4 p-6 max-h-[85vh] flex flex-col">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-semibold text-gray-900">
                                #{detail.message?.id} · {detail.message?.messageType}
                            </h3>
                            <button
                                onClick={() => setDetail({ open: false, loading: false, message: null })}
                                className="text-2xl leading-none text-gray-400 hover:text-gray-500"
                            >
                                &times;
                            </button>
                        </div>
                        {detail.message?.error && (
                            <p className="text-sm text-red-700 mb-2">{detail.message.error}</p>
                        )}
                        {detail.loading ? (
                            <p className="text-sm text-gray-500">{t("akfaIntegrationPage.common.loading")}</p>
                        ) : (
                            <pre className="flex-1 overflow-auto bg-gray-900 text-gray-100 text-xs rounded-md p-3">
                                {prettyBody(detail.message?.body)}
                            </pre>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
